import React from 'react';
import styled from 'styled-components';
import { Typography } from '../Typography/Typography';
import { focusVisibleStyles, standardTransitionStyles } from '../../sharedStyles';

const TabListContainer = styled.ul<{ $variant?: 'default' | 'fullWidthWithBorder' }>`
  display: flex;
  gap: 24px;
  list-style: none;
  padding: 0;
  margin: 0;
  // Push the toggle buttons down to line up better with other text on the same line
  margin-bottom: -2px;

  ${({ $variant }) =>
    $variant === 'fullWidthWithBorder' &&
    `
      width: 100%;
      border-bottom: 1px solid var(--grayscale-100);
      margin-bottom: 0;
    `}
`;

const TabListItem = styled.li`
  margin: 0;
  padding: 0;
`;

const StyledTab = styled(Typography)`
  all: unset;
  cursor: pointer;
  color: var(--grayscale-600);
  margin-bottom: 2px;
  padding: 6px 0;
  display: block;
  ${standardTransitionStyles}

  &[aria-selected='true'] {
    margin-bottom: 0;
    border-bottom: 2px solid var(--grayscale-900);
    color: var(--grayscale-900);
  }

  ${focusVisibleStyles}
`;

export interface TabProps {
  /**
   * Children of the component
   */
  children: React.ReactNode;
  /**
   * Click handler for the tab
   */
  onClick: () => void;
  /**
   * Whether the tab is currently selected
   */
  isSelected: boolean;
  /**
   * Aria label for accessibility
   */
  ariaLabel: string;
  /**
   * ID of the tab, used to associate with its tabpanel
   */
  id?: string;
  /**
   * ID of the tabpanel controlled by this tab
   */
  ariaControls?: string;
  className?: string;
}

export interface TabItem {
  /**
   * Label to display in the tab. If not provided, the value will be used as the label.
   */
  label?: string | React.ReactNode;
  /**
   * Value associated with this tab
   */
  value: string;
}

export interface TabListProps {
  /**
   * Array of tab items to display
   */
  tabs: TabItem[];
  /**
   * Currently selected tab value
   */
  selectedValue: string;
  /**
   * Callback when a tab is selected
   */
  onSelect: (value: string) => void;
  /**
   * ID of this tab list, used to associate with its tabpanel
   */
  tabListId: string;
  /**
   * ID of the tabpanel controlled by this tab list
   *
   * This id should be applied to the container of the content that is controlled by the tabs.
   * That element should have a role of "tabpanel" and an aria-labelledby attribute that references the tab list.
   */
  tabPanelId: string;
  /**
   * Aria label describing the purpose of this tab list
   */
  ariaLabel: string;
  /**
   * Class name to apply to the tab list container
   */
  className?: string;
  /**
   * Styling variant for the tab list
   */
  variant?: 'default' | 'fullWidthWithBorder';
}

/**
 * A tab list component that manages a group of tabs
 *
 * @example
 * ```tsx
 * const [selectedTab, setSelectedTab] = useState('tab1');
 * const tabListId = useId();
 * const tabPanelId = useId();
 *
 * return (
 *   <div>
 *     <TabList
 *       tabs={[
 *         { value: 'tab1', label: 'First Tab' },
 *         { value: 'tab2', label: 'Second Tab' }
 *       ]}
 *       selectedValue={selectedTab}
 *       onSelect={setSelectedTab}
 *       tabListId={tabListId}
 *       tabPanelId={tabPanelId}
 *       ariaLabel="Example tabs"
 *     />
 *     <div
 *       role="tabpanel"
 *       id={tabPanelId}
 *       aria-labelledby={tabListId}
 *     >
 *       {selectedTab === 'tab1' ? (
 *         <div>First tab content</div>
 *       ) : (
 *         <div>Second tab content</div>
 *       )}
 *     </div>
 *   </div>
 * );
 */
export const TabList = ({
  tabs,
  selectedValue,
  onSelect,
  tabPanelId,
  ariaLabel,
  tabListId,
  className,
  variant = 'default',
  // Allow passing through additional props to the container, e.g. analytics props
  ...rest
}: TabListProps) => {
  return (
    <TabListContainer
      id={tabListId}
      role="tablist"
      aria-label={ariaLabel}
      className={className}
      $variant={variant}
      // Used to track the selected tab in analytics
      data-selected-tab={selectedValue}
      {...rest}
    >
      {tabs.map((tab) => {
        const tabId = `${tabListId}-${tab.value}`;
        return (
          <TabListItem key={tab.value} role="presentation">
            <StyledTab
              id={tabId}
              href={`#${tabPanelId}`}
              role="tab"
              aria-selected={tab.value === selectedValue}
              aria-controls={tabPanelId}
              renderedAs="a"
              styledAs="bodySmallSpaceGrotesk"
              weight={500}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                onSelect(tab.value);
              }}
            >
              {tab.label || tab.value}
            </StyledTab>
          </TabListItem>
        );
      })}
    </TabListContainer>
  );
};

export default TabList;
